import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import {
  BanknotesIcon,
  CubeIcon,
  CurrencyPoundIcon,
  ReceiptRefundIcon,
  TagIcon,
} from "@heroicons/react/24/outline";

import HeadlineStat from "../../components/shared/HeadlineStat";
import classNames from "../../utils/classNames";
import formatDateTime from "../../utils/formatDateTime";
import PriceFormat from "../../utils/priceFormat";
import ReportLayout, { ReportFilterSelect } from "./ReportLayout";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import {
  PART_CATEGORIES,
  SALES_CHANNELS,
  SALESPEOPLE,
  buildPartSales,
  monthsAgo,
  today,
} from "./reportsMockData";

const PAGE_SIZE = 50;
const fmtInt = (n: number) => n.toLocaleString("en-GB");

/**
 * Part Sales report — flat line-item view of every part sale within the
 * selected window, filterable by category / salesperson / channel and
 * paginated so long windows stay responsive.
 */
export default function PartSalesReport() {
  const { setInfo } = usePageNameContext();
  const [dateRange, setDateRange] = useState({
    from: monthsAgo(3),
    to: today(),
  });
  const [categoryId, setCategoryId] = useState("all");
  const [salespersonId, setSalespersonId] = useState("all");
  const [channel, setChannel] = useState("all");
  const [page, setPage] = useState(0);

  useEffect(() => {
    setInfo({
      name: "Part Sales Report",
      desc: "Every part sale in the selected window, filterable and exportable",
    });
  }, [setInfo]);

  const allSales = useMemo(
    () => buildPartSales(`${dateRange.from}T00:00:00Z`, `${dateRange.to}T23:59:59Z`),
    [dateRange.from, dateRange.to]
  );

  const filtered = useMemo(() => {
    return allSales.filter((s) => {
      if (categoryId !== "all" && s.partCategoryId !== categoryId) return false;
      if (salespersonId !== "all" && s.salespersonId !== salespersonId) return false;
      if (channel !== "all" && s.salesChannel !== channel) return false;
      return true;
    });
  }, [allSales, categoryId, salespersonId, channel]);

  // Reset pagination whenever the filter set changes so the user doesn't
  // end up on an empty page.
  useEffect(() => {
    setPage(0);
  }, [dateRange.from, dateRange.to, categoryId, salespersonId, channel]);

  const totals = useMemo(
    () => ({
      lines: filtered.length,
      units: filtered.reduce((s, r) => s + r.quantity, 0),
      revenue: filtered.reduce((s, r) => s + r.total, 0),
      profit: filtered.reduce((s, r) => s + r.profit, 0),
    }),
    [filtered]
  );

  const avgOrderValue =
    filtered.length === 0 ? 0 : Math.round(totals.revenue / filtered.length);
  const margin =
    totals.revenue === 0
      ? 0
      : Math.round((totals.profit / totals.revenue) * 100);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const visible = useMemo(
    () => filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [filtered, page]
  );

  const csvRows = useMemo(
    () =>
      filtered.map((s) => ({
        saleDate: s.saleDate.slice(0, 10),
        orderNumber: s.orderNumber,
        sku: s.sku,
        partName: s.partName,
        partCategory: s.partCategory,
        donorStockNumber: s.vehicle.stockNumber,
        donorVehicle: `${s.vehicle.year} ${s.vehicle.make} ${s.vehicle.model}`,
        salesperson: s.salesperson.name,
        salesChannel: s.salesChannel,
        quantity: s.quantity,
        unitPriceGBP: s.unitPrice,
        totalGBP: s.total,
        profitGBP: s.profit,
      })),
    [filtered]
  );

  return (
    <ReportLayout
      title="Part Sales Report"
      description="Line-item sales with full attribution — pick a window, refine with the filters, then export the matching rows."
      icon={ReceiptRefundIcon}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      controls={
        <>
          <ReportFilterSelect
            label="Category"
            value={categoryId}
            onChange={setCategoryId}
            options={[
              { value: "all", label: "All categories" },
              ...PART_CATEGORIES.map((c) => ({ value: c.id, label: c.name })),
            ]}
            width="11rem"
          />
          <ReportFilterSelect
            label="Salesperson"
            value={salespersonId}
            onChange={setSalespersonId}
            options={[
              { value: "all", label: "All salespeople" },
              ...SALESPEOPLE.map((p) => ({ value: p.id, label: p.name })),
            ]}
            width="11rem"
          />
          <ReportFilterSelect
            label="Channel"
            value={channel}
            onChange={setChannel}
            options={[
              { value: "all", label: "All channels" },
              ...SALES_CHANNELS.map((c) => ({ value: c, label: c })),
            ]}
            width="9rem"
          />
        </>
      }
      csvRows={csvRows}
      csvFilename="part-sales"
      headlineStats={
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          <HeadlineStat
            tone="blue"
            icon={ReceiptRefundIcon}
            value={fmtInt(totals.lines)}
            label="Order lines"
            sublabel={`${fmtInt(totals.units)} units`}
          />
          <HeadlineStat
            tone="emerald"
            icon={CurrencyPoundIcon}
            value={PriceFormat(totals.revenue)}
            label="Revenue"
            sublabel={`${fmtInt(margin)}% margin`}
          />
          <HeadlineStat
            tone="violet"
            icon={BanknotesIcon}
            value={PriceFormat(totals.profit)}
            label="Profit"
            sublabel="After unit cost"
          />
          <HeadlineStat
            tone="cyan"
            icon={CubeIcon}
            value={PriceFormat(avgOrderValue)}
            label="Avg line value"
            sublabel="Per order line"
          />
          <HeadlineStat
            tone="amber"
            icon={TagIcon}
            value={
              categoryId === "all"
                ? "All"
                : PART_CATEGORIES.find((c) => c.id === categoryId)?.name ?? "—"
            }
            label="Category filter"
            sublabel={
              salespersonId === "all"
                ? "All salespeople"
                : SALESPEOPLE.find((p) => p.id === salespersonId)?.name ?? "—"
            }
          />
        </div>
      }
    >
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <Th>Sale date</Th>
                <Th>Order</Th>
                <Th>SKU</Th>
                <Th>Part</Th>
                <Th>Category</Th>
                <Th>Donor vehicle</Th>
                <Th>Salesperson</Th>
                <Th>Channel</Th>
                <Th className="text-right">Qty</Th>
                <Th className="text-right">Unit</Th>
                <Th className="text-right">Total</Th>
                <Th className="text-right">Profit</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50/60">
                  <td className="whitespace-nowrap px-3 py-2 text-slate-600">
                    {formatDateTime(s.saleDate, DateTime.DATE_MED)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-slate-500">
                    {s.orderNumber}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-slate-500">
                    {s.sku}
                  </td>
                  <td className="px-3 py-2 text-slate-700">{s.partName}</td>
                  <td className="px-3 py-2 text-slate-500">{s.partCategory}</td>
                  <td className="whitespace-nowrap px-3 py-2">
                    <div className="flex flex-col leading-tight">
                      <span className="text-slate-700">
                        {s.vehicle.year} {s.vehicle.make} {s.vehicle.model}
                      </span>
                      <span className="font-mono text-[11px] text-slate-400">
                        {s.vehicle.registration}
                      </span>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-700">
                    {s.salesperson.name}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-slate-500">
                    {s.salesChannel}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-700">
                    {s.quantity}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-700">
                    {PriceFormat(s.unitPrice)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-slate-900">
                    {PriceFormat(s.total)}
                  </td>
                  <td
                    className={classNames(
                      "whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums",
                      s.profit >= 0 ? "text-emerald-700" : "text-rose-600"
                    )}
                  >
                    {PriceFormat(s.profit)}
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td
                    colSpan={12}
                    className="px-4 py-6 text-center text-sm text-slate-500"
                  >
                    No sales match the current filter.
                  </td>
                </tr>
              )}
            </tbody>

            {filtered.length > 0 && (
              <tfoot className="bg-slate-50">
                <tr className="text-sm font-semibold text-slate-700">
                  <td className="px-3 py-2" colSpan={8}>
                    Totals (across {fmtInt(filtered.length)}{" "}
                    {filtered.length === 1 ? "line" : "lines"})
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtInt(totals.units)}
                  </td>
                  <td />
                  <td className="px-3 py-2 text-right tabular-nums">
                    {PriceFormat(totals.revenue)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {PriceFormat(totals.profit)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-2 text-sm">
            <p className="text-slate-500">
              Showing{" "}
              <span className="font-semibold text-slate-700">
                {fmtInt(page * PAGE_SIZE + 1)}–
                {fmtInt(Math.min((page + 1) * PAGE_SIZE, filtered.length))}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-slate-700">
                {fmtInt(filtered.length)}
              </span>
            </p>
            <div className="flex items-center gap-1">
              <PageButton
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                Prev
              </PageButton>
              <span className="px-2 text-slate-500">
                Page {page + 1} / {pageCount}
              </span>
              <PageButton
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={page >= pageCount - 1}
              >
                Next
              </PageButton>
            </div>
          </div>
        )}
      </div>
    </ReportLayout>
  );
}

function Th({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      className={classNames(
        "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500",
        className ?? ""
      )}
    >
      {children}
    </th>
  );
}

function PageButton({
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      {...rest}
      className={classNames(
        "rounded-md border px-2 py-1 text-xs font-semibold transition",
        rest.disabled
          ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
          : "border-slate-300 bg-white text-slate-700 hover:border-primary-300 hover:text-primary-700"
      )}
    >
      {children}
    </button>
  );
}
